import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { syncNomenclatureIfStale } from '@/lib/nomenclature/sync';
import { resolverQueryKey } from './useResolver';

const PERIOD_MS = 5 * 60_000; // mirror the mobile app's 5-minute cadence

/**
 * Keeps the nomenclature cache fresh while the authenticated shell is mounted:
 * one sync on mount, then every 5 minutes. Runs in the background; the engine
 * itself throttles and single-flights, so repeated calls are cheap. When a sync
 * actually replaces tables, the UUID→name resolver query is invalidated so
 * already-rendered cards pick up the freshly-synced names.
 */
export function useNomenclatureSync(): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    let active = true;
    const runSync = async () => {
      const result = await syncNomenclatureIfStale();
      if (active && result.synced.length > 0) {
        void queryClient.invalidateQueries({ queryKey: resolverQueryKey });
      }
    };
    void runSync();
    const id = setInterval(() => {
      if (active) void runSync();
    }, PERIOD_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [queryClient]);
}
